import React, { JSX } from 'react';
import { useCallback } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

import { useRefresh } from '../hooks/useRefresh';

import { Tooltip } from './Tooltip';

interface RefreshButtonProps {
  className?: string;
}

export function RefreshButton({ className = '' }: RefreshButtonProps): JSX.Element {
  const { isAutoRefreshEnabled, isRefreshing, lastRefreshed, refreshData, toggleAutoRefresh } =
    useRefresh();

  const formattedTime = lastRefreshed
    ? new Intl.DateTimeFormat(navigator.language || 'en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      }).format(lastRefreshed)
    : 'Never';

  const handleRefresh = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      void refreshData();
    },
    [refreshData],
  );

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <Tooltip content="Refresh data" placement="bottom">
        <button
          type="button"
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="inline-flex items-center px-3 py-1.5 border border-default rounded-md
                    shadow-sm text-sm font-medium bg-surface dark:bg-surface-elevated text-secondary-600 dark:text-muted
                    hover:bg-surface-elevated  focus:outline-none focus:ring-2
                    focus:ring-offset-2 focus:ring-primary dark:focus:ring-primary/50
                    disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Refresh data"
        >
          <ArrowPathIcon className={`h-4 w-4 mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </Tooltip>

      <div className="hidden md:flex items-center">
        <Tooltip content={`Last updated: ${formattedTime}`} placement="bottom">
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={isAutoRefreshEnabled}
              onChange={toggleAutoRefresh}
              className="sr-only peer"
            />
            <div
              className="relative w-11 h-6 bg-default-200 dark:bg-default-700 peer-focus:outline-none peer-focus:ring-4
                          peer-focus:ring-primary/20 dark:peer-focus:ring-primary/20 rounded-full peer
                          peer-checked:after:translate-x-full peer-checked:after:border-white after:content-['']
                          after:absolute after:top-0.5 after:left-0.5 after:bg-white dark:after:bg-default-300
                          after:border-default-300 dark:after:border-default-600 after:border after:rounded-full
                          after:h-5 after:w-5 after:transition-all
                          peer-checked:bg-primary dark:peer-checked:bg-primary peer-checked:after:bg-white"
            ></div>
            <span className="ml-2 text-sm font-medium text-foreground dark:text-muted">Auto</span>
          </label>
        </Tooltip>
      </div>
    </div>
  );
}
